import { abpParams, abpParams2 } from "./constants";

export let AdBlockPlusStatus = "not_set";

// Max time to wait on a single /px.js probe request. Bounds detection so a
// stalled request can't hang callers that await it (e.g. the FOS redirect
// event). A timeout is treated as indeterminate (see the catch below), NOT as a
// block, so a slow no-adblock shopper isn't misreported as ABP-dependent.
const PROBE_TIMEOUT_MS = 3000;

// Distinct error so the probe can tell a *timeout* (slow network, indeterminate)
// apart from a genuine fetch rejection (what ad-block actually looks like).
class ProbeTimeoutError extends Error {}

const withTimeout = <T>(request: Promise<T>): Promise<T> => {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new ProbeTimeoutError()), PROBE_TIMEOUT_MS);
  });
  return Promise.race([request, timeout]).finally(() => clearTimeout(timer));
};

// Shared detection promise. Cached so that app startup and any caller that needs
// a reliable value (e.g. the FOS redirect event) all await the SAME settled
// result instead of observing the transient block_all/acceptable states the
// probe passes through on its way to the final value.
let detection: Promise<string> | null = null;

// Runs the adblock-plus detection probe once and returns the shared promise that
// resolves with the final AdBlockPlusStatus once detection has settled. Safe to
// call repeatedly / concurrently — the probe only runs the first time.
export const setAdBlockPlusStatus = (): Promise<string> => {
  if (detection) return detection;

  detection = (async () => {
    // eslint-disable-next-line no-process-env
    const baseUrl = `${process.env.PUBLIC_URL}/px.js`;
    try {
      AdBlockPlusStatus = "block_all";
      await withTimeout(fetch(`${baseUrl}?ch=1&${abpParams}`));
      AdBlockPlusStatus = "acceptable";
      await withTimeout(fetch(`${baseUrl}?ch=2&${abpParams2}`)); // If enabled, ABP will block this request
      AdBlockPlusStatus = "not_present";
    } catch (e) {
      // A timed-out probe is indeterminate, so don't let a slow no-adblock
      // shopper masquerade as "acceptable"/"block_all" — reset to "not_set".
      // A genuine fetch rejection (blocked) keeps the transient value reached.
      if (e instanceof ProbeTimeoutError) {
        AdBlockPlusStatus = "not_set";
      }
    }
    return AdBlockPlusStatus;
  })();

  return detection;
};
